import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSearchConfirmLook = 'searchzone-research' | 'searchzone-auto' | 'searchzone-showall' | 'searchzone-anyway' | 'searchzone-anyway-empty';
export type WedgeSearchConfirmFieldVariant = 'wedge-confirm-zone-field';

const LOOKS: readonly string[] = ['searchzone-research', 'searchzone-auto', 'searchzone-showall', 'searchzone-anyway', 'searchzone-anyway-empty'];
export const isWedgeSearchConfirmLook = (look: string): look is WedgeSearchConfirmLook => LOOKS.includes(look);
export const isWedgeSearchConfirmFieldVariant = (variant: unknown): variant is WedgeSearchConfirmFieldVariant => variant === 'wedge-confirm-zone-field';

/* Released research button of the account confirm sheet, off-scale: the chain guardrail asks for a zone and then researches the account in it. It
   is a full-width block with a 10px top margin, 13px padding, a 13px radius and 13.5px bold type on the Wedge font, in the source declaration order.
   Held off (no zone typed) it has a 1px dashed separator border, no fill, the secondary ink and a default cursor. With a zone it has no border, the
   accent fill and a pointer cursor; its white ink is a literal that the caller owns. It draws no transparent target (wedgeSearchConfirmDrawsHit): the
   anchor would add a positioned box that the legacy button does not have, and the button already spans the sheet width between two 10px gaps.
   The other four looks of the sheet follow the same rule. The auto match button spans the width of its banner, the show-all toggle and the search anyway
   button sit 10px under their neighbors, and a 44px ring around a 36px toggle would overlap the button above and the first candidate below. */
export const wedgeSearchConfirmDrawsHit = (look: string): boolean => !isWedgeSearchConfirmLook(look);

export function wedgeSearchConfirmStyle(look: WedgeSearchConfirmLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  /* "Yes, name on street" of the auto match banner: a left-aligned accent block; its ink is the caller's source text (on-accent with a bg fallback). */
  if (look === 'searchzone-auto') return {
    width: '100%', padding: 13, borderRadius: 12, border: 0, background: r('accent'),
    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: r('font'), textAlign: 'left', lineHeight: 1.35,
  };
  /* The quiet toggle that reveals or hides the cross-state locations. */
  if (look === 'searchzone-showall') return {
    width: '100%', marginTop: 10, padding: 9, borderRadius: 11, border: 0, background: 'transparent', color: r('labelTer'),
    fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: r('font'),
  };
  /* Search anyway or research a different area: filled with the label ink once a city is typed, dashed and empty before. The button stays enabled in both. */
  if (look === 'searchzone-anyway') return {
    width: '100%', marginTop: 10, padding: 13, borderRadius: 13, border: 0, background: r('label'), color: r('surface'),
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: r('font'),
  };
  if (look === 'searchzone-anyway-empty') return {
    width: '100%', marginTop: 10, padding: 13, borderRadius: 13, border: `1px dashed ${r('separator')}`, background: 'transparent', color: r('labelSec'),
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: r('font'),
  };
  return {
    width: '100%', marginTop: 10, padding: 13, borderRadius: 13,
    border: disabled ? `1px dashed ${r('separator')}` : 0, background: disabled ? 'transparent' : r('accent'), ...(disabled ? {color: r('labelSec')} : {}),
    fontSize: 13.5, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontFamily: r('font'),
  };
}

/* Released zone input of the account confirm sheet: one native input, full width and border-box, 11px by 13px of padding, a 1px card border on the
   surface fill, a 12px radius, 14.5px type in the label ink on the Wedge font, and no outline. Declaration order is the source order. */
export const wedgeSearchConfirmFieldStyle = (): CSSProperties => {
  const r = wedgeExactRole;
  return {
    width: '100%', padding: '11px 13px', background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: 12, fontSize: 14.5,
    color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
  };
};
